// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  HARMONIZED_CATEGORIES,
  getCostCategoryValues,
} from '@/costs/data/cost-categories';
import { BaseSearchableFilter } from '@/filters/base-searchable-filter';
import { useMemo } from 'react';

interface Props {
  /**
   * An array of strings representing the currently selected categories.
   */
  value: string[];

  /**
   * The label for the filter menu.
   */
  label?: string;

  /**
   * A callback fired when the selected categories change.
   */
  onChange: (value: string[]) => void;

  /**
   * Whether to show the search input. Defaults to true.
   */
  enableSearch?: boolean;

  /**
   * Whether to show a "Not set" option as the first item. When selected,
   * it will pass "null" as an ID to the server. Defaults to false.
   */
  enableNotSet?: boolean;
}

/**
 * A searchable filter for selecting zero or more cost categories.
 */
export function CostCategoryFilter(props: Props) {
  const categories = useMemo(() => getCostCategoryValues(), []);

  // Memoized on the categories: the options no longer carry selection
  const options = useMemo(() => {
    const categoryOptions = categories.map((category) => ({
      value: category,
      label: category,
      group: HARMONIZED_CATEGORIES[category].group,
      example: HARMONIZED_CATEGORIES[category].example,
    }));

    // "Not set" appears first, outside the groups
    return props.enableNotSet
      ? [{ value: '', label: 'Not set', isSpecial: true }, ...categoryOptions]
      : categoryOptions;
  }, [categories, props.enableNotSet]);

  const handleChangeOption = (id: string, isActive: boolean) => {
    if (isActive) {
      return props.onChange([...props.value, id]);
    }

    props.onChange(props.value.filter((category) => category !== id));
  };

  return (
    <BaseSearchableFilter
      label={props.label ?? 'Categories'}
      options={options}
      selectedValues={props.value}
      onChangeOption={handleChangeOption}
      searchPlaceholder="Search categories..."
      enableSearch={props.enableSearch}
      isLoading={false}
      maxHeight={400}
    />
  );
}
